import { P } from 'components'
import BreadCrumbs from 'components/breadcrumbs'
import PMBBadge from 'components/PMBBadge'
import PMBInputNumber from 'components/PMBInputNumber'
import PMBTotalFooter from 'components/PMBTotalFooter'
import MonthSelectPlugin from 'flatpickr/dist/plugins/monthSelect'
import { capitalizeEveryWord } from 'helpers'
import useIsMobile from 'hooks/useIsMobile'
import moment from 'moment-timezone'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { Calendar, ChevronLeft } from 'react-feather'
import Flatpickr from 'react-flatpickr'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Spinner,
} from 'reactstrap'
import { formatInputNumber, parseFormattedNumber } from 'utility/Utils'
import { newFormatThousandSeparator } from 'utils/formatNumber'
import { URL_EMPLOYEE_PAYMENTS } from 'utils/pathUrl'
import { useLazyGetDataAutoLoadQuery } from './employeePaymentsListApiSlice'
import {
  setAutoCalculateDisbursements,
  setFilterPeriod,
} from './employeePaymentsListSlice'
import ModalConfirmation from './ModalConfirmation'

const AutoCalculation = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const isMobile = useIsMobile()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [localDisbursementData, setLocalDisbursementData] = useState({
    batchName: '',
    disburses: [],
  })

  const { filterPeriod } = useSelector((state) => state.employeePaymentsList)
  const selectedMonthYear = filterPeriod?.selectedMonthYear

  const { enqueueSnackbar } = useSnackbar()

  const { disbursementData } = useSelector(
    (state) => state.employeePaymentsList,
  )

  const isExistManual =
    disbursementData?.disburses?.filter((item) => item?.source === 'manual')
      ?.length > 0

  const dataDisbursementAuto = localDisbursementData?.disburses || []

  const hasZeroAmount = dataDisbursementAuto.some((item) => item.amount === 0)

  const totalAmount = dataDisbursementAuto?.reduce((acc, item) => {
    return acc + item.amount
  }, 0)

  const handleBack = () => {
    navigate(-1)
  }

  const handleSubmit = async () => {
    if (isExistManual) {
      setIsModalOpen(true)
      return
    }

    await applyDisbursement()
  }

  const applyDisbursement = async () => {
    try {
      dispatch(
        setAutoCalculateDisbursements({
          batchName: localDisbursementData.batchName,
          disburses: localDisbursementData.disburses,
        }),
      )
      enqueueSnackbar('Apply disbursement successfully', {
        variant: 'success',
      })
      handleBack()
    } catch (error) {
      enqueueSnackbar(
        error?.data?.message || error?.message || 'Failed to submit payment',
        {
          variant: 'error',
        },
      )
    }
  }

  const handleChangeMonthYear = (selectedDates) => {
    if (selectedDates.length > 0) {
      const date = selectedDates[0]
      dispatch(
        setFilterPeriod({
          month: date.getMonth() + 1,
          year: date.getFullYear(),
          selectedMonthYear: date,
        }),
      )
    }
  }

  const handleQuantityChange = (employeeUserId, itemId, value) => {
    const numericValue = parseFormattedNumber(value)

    setLocalDisbursementData((prev) => ({
      ...prev,
      disburses: prev.disburses.map((item) =>
        item.employeeUserId === employeeUserId && item.itemId === itemId
          ? { ...item, unit: numericValue }
          : item,
      ),
    }))
  }

  const handleAmountChange = (employeeUserId, itemId, value) => {
    const numericValue = parseFormattedNumber(value)

    setLocalDisbursementData((prev) => ({
      ...prev,
      disburses: prev.disburses.map((item) =>
        item.employeeUserId === employeeUserId && item.itemId === itemId
          ? { ...item, amount: numericValue }
          : item,
      ),
    }))
  }

  const [fetchDataAutoLoad, { isLoading, isFetching }] =
    useLazyGetDataAutoLoadQuery()

  const handleLoadData = async () => {
    await loadAutoCalculationData()
  }

  const loadAutoCalculationData = async () => {
    try {
      const response = await fetchDataAutoLoad({
        month: filterPeriod.month,
        year: filterPeriod.year,
      }).unwrap()

      if (response?.disburses && response?.disburses.length > 0) {
        enqueueSnackbar('Data loaded successfully', {
          variant: 'success',
        })

        setLocalDisbursementData({
          batchName: response.batchName,
          disburses: response.disburses.map((item) => ({
            ...item,
            source: 'auto',
          })),
        })
      } else {
        enqueueSnackbar('No data found for the selected period', {
          variant: 'warning',
        })
      }
    } catch {
      enqueueSnackbar('Failed to load data', {
        variant: 'error',
      })
    }
  }

  const handleConfirmLoadData = async () => {
    setIsModalOpen(false)
    await applyDisbursement()
  }

  const handleCancelModal = () => {
    setIsModalOpen(false)
  }

  return (
    <>
      {/* Confirmation Modal */}
      <ModalConfirmation
        isModalOpen={isModalOpen}
        handleCancelModal={handleCancelModal}
        handleConfirmLoadData={handleConfirmLoadData}
        isLoading={isLoading}
        isFetching={isFetching}
      />

      <Container fluid className="custom-container">
        <div className="mt-3">
          <BreadCrumbs
            breadCrumbParent2="List of Disbursements"
            breadCrumbParent2Link={URL_EMPLOYEE_PAYMENTS}
            breadCrumbActive="Auto Calculation"
          />
        </div>

        <div className="d-flex align-items-start pb-4">
          <ChevronLeft
            size={18}
            className="me-2 mt-1"
            onClick={handleBack}
            style={{ cursor: 'pointer' }}
          />
          <div>
            <P size="medium" className="fw-bold m-0">
              Auto Calculate from Production Report
            </P>

            <P size="small" className="text-muted m-0">
              Retrieve production data and calculate disbursement amounts
            </P>
          </div>
        </div>

        <Card className="border-0 shadow-none">
          <CardBody>
            <div className="">
              <P size="medium" className="mb-4">
                Production Data & Calculations
              </P>
            </div>

            <Col sm="12" md="12" lg="12">
              <div className="d-flex flex-column">
                <P size="regular" className="mb-1">
                  Select Period
                </P>
                <div className="d-flex gap-2 align-items-end">
                  <div className="flex-grow-1">
                    <div
                      className="d-flex align-items-center border rounded"
                      style={{
                        height: 42,
                        background: '#ffffff',
                      }}
                    >
                      <Calendar size={17} className="ms-3" />
                      <Flatpickr
                        value={
                          selectedMonthYear
                            ? moment(selectedMonthYear).format('YYYY-MM')
                            : moment().format('YYYY-MM')
                        }
                        onChange={handleChangeMonthYear}
                        options={{
                          altInput: true,
                          altFormat: 'F Y',
                          dateFormat: 'Y-m',
                          defaultDate: selectedMonthYear
                            ? moment(selectedMonthYear).format('YYYY-MM')
                            : moment().format('YYYY-MM'),
                          disableMobile: true,
                          maxDate: moment().format('YYYY-MM'),
                          plugins: [
                            new MonthSelectPlugin({
                              shorthand: false,
                              dateFormat: 'Y-m',
                              altFormat: 'F Y',
                            }),
                          ],
                        }}
                        className="form-control bg-transparent border-0 shadow-none"
                      />
                    </div>
                  </div>
                  <Button
                    color="primary"
                    onClick={handleLoadData}
                    disabled={isLoading || isFetching}
                    style={{
                      height: 42,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {isLoading ||
                      (isFetching && <Spinner size="sm" className="me-2" />)}
                    {isLoading || isFetching ? 'Loading...' : 'Load Data'}
                  </Button>
                </div>
              </div>
            </Col>
            <Col sm="12" md="12" lg="12">
              <div className="d-flex flex-column gap-3 mt-3">
                {dataDisbursementAuto?.map((item, i) => {
                  return (
                    <Card key={i} className="border">
                      <CardBody className="p-3">
                        {/* HEADER SECTION */}
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <div>
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <span className="fw-semibold">
                                {item?.employeeName}
                              </span>
                              <PMBBadge
                                variant={
                                  item?.disburseMethod === 'BANK_TRANSFER'
                                    ? 'warning'
                                    : item?.disburseMethod === 'CASH'
                                      ? 'verify'
                                      : 'danger'
                                }
                                title={capitalizeEveryWord(
                                  item?.disburseMethod,
                                )}
                                className="px-2 w-auto"
                                size="small"
                              />
                            </div>

                            <div className="text-muted small mb-2">
                              {item?.rolesName}
                            </div>
                          </div>

                          <div className="text-end ms-3">
                            <span className="fw-bold text-primary">
                              Rp
                              {newFormatThousandSeparator(item?.amount || 0, 2)}
                            </span>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-md-4">
                            <P
                              className="text-xs pb-1 mb-1"
                              size="regular"
                              color="#344054"
                            >
                              Quantity
                              <span style={{ color: '#b42218' }}>*</span>
                            </P>
                            <PMBInputNumber
                              onFocus={(e) => e.target.select()}
                              value={formatInputNumber(item?.unit || 0)}
                              onChange={(e) =>
                                handleQuantityChange(
                                  item?.employeeUserId,
                                  item?.itemId,
                                  e.target.value,
                                )
                              }
                              name={`quantity-${item?.employeeUserId}-${item?.itemId}`}
                              placeholder="Input Quantity"
                            />
                          </div>
                          <div className="col-md-4">
                            <P
                              className="text-xs pb-1 mb-1"
                              size="regular"
                              color="#344054"
                            >
                              Amount
                              <span style={{ color: '#b42218' }}>*</span>
                            </P>
                            <PMBInputNumber
                              onFocus={(e) => e.target.select()}
                              value={formatInputNumber(item?.amount || 0)}
                              onChange={(e) =>
                                handleAmountChange(
                                  item?.employeeUserId,
                                  item?.itemId,
                                  e.target.value,
                                )
                              }
                              name={`amount-${item?.employeeUserId}-${item?.itemId}`}
                              placeholder="Input Amount"
                            />
                          </div>
                          <div className="col-md-4">
                            <P
                              className="text-xs pb-1 mb-1"
                              size="regular"
                              color="#344054"
                            >
                              Product Type
                            </P>
                            <Input
                              value={item?.nameItem || ''}
                              disabled
                              readOnly
                              name={`productType-${item?.employeeUserId}-${item?.itemId}`}
                              placeholder="Product Type"
                              className="bg-light"
                            />
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  )
                })}
              </div>
            </Col>
          </CardBody>
        </Card>
        <div style={{ height: isMobile ? '120px' : '80px' }}></div>
      </Container>

      {dataDisbursementAuto?.length > 0 && (
        <PMBTotalFooter
          total={totalAmount}
          onSubmit={handleSubmit}
          noMinimum
          testId="emp-pay-submit-button"
          customTotalText={
            <div>
              <div className="d-flex gap-2">
                Total Amount:
                <P bold className="text-primary">
                  {' '}
                  Rp{newFormatThousandSeparator(totalAmount)}
                </P>
              </div>
            </div>
          }
          textBtn="Apply to Disbursement List"
          isDisable={
            !localDisbursementData?.batchName ||
            hasZeroAmount ||
            isLoading ||
            isFetching
          }
        />
      )}
    </>
  )
}

export default AutoCalculation
